/**
 * Receipt Verification Service
 * Strict Payment Receipt Verification using AI
 * Follows STRICT + BANK NAME + NO CNIC NAME MATCHING protocol
 */

// Provider configurations - Only providers with vision/image support
const PROVIDER_CONFIGS = {
  openai: {
    baseUrl: 'https://api.openai.com/v1',
    endpoint: '/chat/completions',
    supportsVision: true,
    buildPayload: (model, prompt, imageUrls) => ({
      model: model,
      messages: [
        {
          role: 'user',
          content: [
            { type: 'text', text: prompt },
            ...imageUrls.map(url => ({
              type: 'image_url',
              image_url: { url: url, detail: 'high' }
            }))
          ]
        }
      ],
      max_tokens: 1500
    }),
    authHeader: (key) => ({
      'Authorization': `Bearer ${key}`,
      'Content-Type': 'application/json'
    }),
    extractResponse: (data) => {
      console.log('[OpenAI] Full response:', JSON.stringify(data, null, 2));
      return data.choices?.[0]?.message?.content || '';
    }
  },

  anthropic: {
    baseUrl: 'https://api.anthropic.com/v1',
    endpoint: '/messages',
    supportsVision: true,
    buildPayload: (model, prompt, imageUrls) => ({
      model: model,
      max_tokens: 1500,
      messages: [
        {
          role: 'user',
          content: [
            ...imageUrls.map(url => {
              if (url.startsWith('data:')) {
                const matches = url.match(/^data:([^;]+);base64,(.+)$/);
                if (matches) {
                  return {
                    type: 'image',
                    source: {
                      type: 'base64',
                      media_type: matches[1],
                      data: matches[2]
                    }
                  };
                }
              }
              return {
                type: 'image',
                source: { type: 'url', url: url }
              };
            }),
            { type: 'text', text: prompt }
          ]
        }
      ]
    }),
    authHeader: (key) => ({
      'x-api-key': key,
      'anthropic-version': '2023-06-01',
      'Content-Type': 'application/json'
    }),
    extractResponse: (data) => {
      console.log('[Anthropic] Full response:', JSON.stringify(data, null, 2));
      return data.content?.[0]?.text || '';
    }
  },

  google: {
    baseUrl: 'https://generativelanguage.googleapis.com/v1beta',
    endpoint: '/models/{model}:generateContent',
    supportsVision: true,
    buildPayload: (model, prompt, imageUrls) => ({
      contents: [
        {
          parts: [
            { text: prompt },
            ...imageUrls.map(url => ({
              inline_data: {
                mime_type: 'image/jpeg',
                data: url.split(',')[1]
              }
            }))
          ]
        }
      ],
      generationConfig: {
        temperature: 0.1,
        maxOutputTokens: 1500
      }
    }),
    authHeader: (key) => ({
      'Content-Type': 'application/json'
    }),
    extractResponse: (data) => {
      console.log('[Google] Full response:', JSON.stringify(data, null, 2));
      // Check for blocked or empty candidates
      if (data.candidates && data.candidates.length > 0) {
        const candidate = data.candidates[0];
        if (candidate.finishReason === 'SAFETY') {
          return 'BLOCKED_BY_SAFETY';
        }
        return candidate.content?.parts?.[0]?.text || '';
      }
      // Check for prompt feedback
      if (data.promptFeedback?.blockReason) {
        return `BLOCKED: ${data.promptFeedback.blockReason}`;
      }
      return '';
    }
  },

  openrouter: {
    baseUrl: 'https://openrouter.ai/api/v1',
    endpoint: '/chat/completions',
    supportsVision: true,
    buildPayload: (model, prompt, imageUrls) => ({
      model: model,
      messages: [
        {
          role: 'user',
          content: [
            { type: 'text', text: prompt },
            ...imageUrls.map(url => ({
              type: 'image_url',
              image_url: { url: url, detail: 'high' }
            }))
          ]
        }
      ],
      max_tokens: 1500
    }),
    authHeader: (key) => ({
      'Authorization': `Bearer ${key}`,
      'HTTP-Referer': window.location.origin,
      'X-Title': 'ANB Vision Receipt Verification',
      'Content-Type': 'application/json'
    }),
    extractResponse: (data) => {
      console.log('[OpenRouter] Full response:', JSON.stringify(data, null, 2));

      // Check for error in response
      if (data.error) {
        console.error('[OpenRouter] Error in response:', data.error);
        return `ERROR: ${data.error.message || JSON.stringify(data.error)}`;
      }

      // Standard OpenAI-compatible format
      const content = data.choices?.[0]?.message?.content;
      if (content) return content;

      // Check for finish_reason that might indicate an issue
      const finishReason = data.choices?.[0]?.finish_reason;
      if (finishReason === 'content_filter') {
        return 'BLOCKED: Content filtered';
      }

      // Some models return text differently
      if (data.choices?.[0]?.text) {
        return data.choices[0].text;
      }

      // Check if model doesn't support vision (some OpenRouter models)
      if (data.choices?.length === 0 || !data.choices) {
        console.error('[OpenRouter] No choices in response - model may not support vision');
        return '';
      }

      return '';
    }
  }
};

// Providers that DON'T support vision - for better error messages
const NON_VISION_PROVIDERS = ['cohere', 'huggingface'];

/**
 * Build the strict verification prompt with case data
 * NOTE: CNIC validation is done separately from user data, NOT by AI
 */
function buildVerificationPrompt(caseData) {
  return `You are a financial verification AI. Analyze this payment receipt and extract data.

CASE DATA (TRUSTED SYSTEM VALUES):
- Expected Amount: ${caseData.expectedAmount || 'NOT PROVIDED'}
- Verified Sender Account Number: ${caseData.senderAccount || 'NOT PROVIDED'}
- Expected Account Title: ${caseData.expectedAccountTitle || 'NOT PROVIDED'}
- Expected Bank Name: ${caseData.expectedBank || 'NOT PROVIDED'}

STRICT VALIDATION RULES:

1. AMOUNT VERIFICATION
   - Extract transaction amount from receipt
   - Must be EXACT match with system value
   - Any mismatch = FAIL

2. SENDER ACCOUNT NUMBER VERIFICATION (POSITION-AWARE)
   - Extract sender/from-account number as shown
   - Handle masked accounts (e.g., ****2232)
   - Every visible digit must match at correct position
   - Fully masked or no visible digits = MANUAL_REVIEW
   - Never guess hidden digits

3. ACCOUNT TITLE VERIFICATION
   - Extract Account Title from receipt
   - Must match Expected Account Title exactly
   - Case-insensitive, trim spaces allowed
   - Any difference = FAIL

4. BANK NAME VERIFICATION
   - Extract Bank Name from receipt
   - Must match Expected Bank Name exactly
   - Case-insensitive, trim spaces allowed
   - Missing = MANUAL_REVIEW
   - Different = FAIL

IMPORTANT:
- Never hallucinate missing values
- Never infer abbreviations
- Prefer rejection over false approval
- Flag unreadable or missing mandatory fields

Return ONLY this JSON structure:
{
  "extracted": {
    "amount": "extracted amount from receipt",
    "account_number": "extracted account number (may be masked)",
    "account_title": "extracted account title/name",
    "bank_name": "extracted bank name",
    "transaction_date": "extracted date/time",
    "reference_id": "extracted reference/transaction ID"
  },
  "verification": {
    "amount_match": true/false,
    "account_number_match": true/false/"UNVERIFIABLE",
    "account_title_match": true/false,
    "bank_name_match": true/false/"MISSING"
  },
  "overall_status": "APPROVED" or "FAILED" or "MANUAL_REVIEW",
  "failure_reasons": ["list of specific reasons if FAILED or MANUAL_REVIEW"],
  "confidence": 0-100,
  "notes": "any additional observations about the receipt"
}`;
}

/**
 * Compress image for API efficiency
 */
async function compressReceiptImage(base64Data, maxWidth = 1000, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
      resolve(compressedBase64);
    };
    img.onerror = reject;
    img.src = base64Data;
  });
}

/**
 * Convert image URL to base64
 */
async function imageToBase64(url) {
  if (url.startsWith('data:')) {
    return await compressReceiptImage(url);
  }

  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);

  const blob = await response.blob();
  const base64Data = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  return await compressReceiptImage(base64Data);
}

/**
 * Parse JSON from AI response
 */
function parseVerificationResponse(response) {
  if (!response) {
    return getDefaultResult(['No response from AI']);
  }

  let jsonStr = response;

  // Check for markdown code blocks
  const jsonMatch = response.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (jsonMatch) {
    jsonStr = jsonMatch[1].trim();
  }

  // Try to find JSON object pattern
  const objectMatch = response.match(/\{[\s\S]*\}/);
  if (objectMatch) {
    jsonStr = objectMatch[0];
  }

  try {
    const parsed = JSON.parse(jsonStr);
    return {
      extracted: parsed.extracted || {},
      verification: parsed.verification || {},
      overall_status: parsed.overall_status || 'MANUAL_REVIEW',
      failure_reasons: parsed.failure_reasons || [],
      confidence: parsed.confidence || 0,
      notes: parsed.notes || ''
    };
  } catch (e) {
    console.error('[Receipt Verification] Failed to parse JSON:', e);
    return getDefaultResult(['Failed to parse AI response']);
  }
}

/**
 * Get default result object
 */
function getDefaultResult(errors = []) {
  return {
    extracted: {},
    verification: {
      cnic_valid: null,
      amount_match: null,
      account_number_match: null,
      account_title_match: null,
      bank_name_match: null
    },
    overall_status: 'MANUAL_REVIEW',
    failure_reasons: errors,
    confidence: 0,
    notes: ''
  };
}

/**
 * Main verification function
 * @param {Object} aiConfig - Active AI configuration
 * @param {string} receiptImage - Receipt image URL or base64
 * @param {Object} caseData - Case data for verification
 */
export async function verifyReceipt(aiConfig, receiptImage, caseData = {}) {
  if (!aiConfig) {
    return {
      success: false,
      error: 'No AI configuration provided',
      result: getDefaultResult(['AI not configured'])
    };
  }

  if (!receiptImage) {
    return {
      success: false,
      error: 'No receipt image provided',
      result: getDefaultResult(['No receipt image'])
    };
  }

  // Support both 'provider' and 'providerId' field names
  const provider = (aiConfig.provider || aiConfig.providerId)?.toLowerCase();
  const config = PROVIDER_CONFIGS[provider];

  console.log('[Receipt Verification] Provider:', provider, 'Config found:', !!config);
  console.log('[Receipt Verification] AI Config:', JSON.stringify({
    provider: provider,
    model: aiConfig.model,
    baseUrl: aiConfig.baseUrl,
    hasApiKey: !!aiConfig.apiKey
  }, null, 2));

  // Check if provider doesn't support vision
  if (NON_VISION_PROVIDERS.includes(provider)) {
    return {
      success: false,
      error: `${provider} does not support image analysis. Please use OpenAI (GPT-4o), Anthropic (Claude), Google (Gemini), or OpenRouter with a vision model.`,
      result: getDefaultResult([`${provider} does not support vision/image analysis`])
    };
  }

  if (!config) {
    return {
      success: false,
      error: `Provider "${provider}" not supported for receipt verification. Supported: OpenAI, Anthropic, Google, OpenRouter`,
      result: getDefaultResult(['Unsupported AI provider for image verification'])
    };
  }

  try {
    console.log('[Receipt Verification] Converting image...');
    const base64Image = await imageToBase64(receiptImage);
    console.log('[Receipt Verification] Image size:', Math.round(base64Image.length / 1024), 'KB');

    const prompt = buildVerificationPrompt(caseData);

    let endpoint = config.endpoint;
    if (provider === 'google') {
      endpoint = endpoint.replace('{model}', aiConfig.model);
    }

    let url = `${aiConfig.baseUrl || config.baseUrl}${endpoint}`;
    if (provider === 'google') {
      url += `?key=${aiConfig.apiKey}`;
    }

    const payload = config.buildPayload(aiConfig.model, prompt, [base64Image]);
    const headers = config.authHeader(aiConfig.apiKey);

    console.log(`[Receipt Verification] Using ${provider} with model ${aiConfig.model}`);
    console.log('[Receipt Verification] Case data:', caseData);

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Receipt Verification] API Error:', errorText);
      let errorData = {};
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {}
      const errorMessage = errorData.error?.message || errorData.message || `API returned ${response.status}`;
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('[Receipt Verification] Full API Response:', JSON.stringify(data, null, 2));

    const rawContent = config.extractResponse(data);
    console.log('[Receipt Verification] Extracted Content:', rawContent);
    console.log('[Receipt Verification] Content length:', rawContent?.length || 0);

    if (!rawContent || rawContent.trim() === '') {
      console.error('[Receipt Verification] Empty response from AI');
      console.error('[Receipt Verification] Raw API data:', JSON.stringify(data, null, 2));
      return {
        success: false,
        error: `AI returned empty response. Provider: ${provider}, Model: ${aiConfig.model}. Check if this model supports image analysis.`,
        result: getDefaultResult(['AI returned empty response - model may not support images'])
      };
    }

    // Check for blocked content
    if (rawContent.startsWith('BLOCKED')) {
      console.error('[Receipt Verification] Content was blocked:', rawContent);
      return {
        success: false,
        error: `Content blocked by AI safety filters: ${rawContent}`,
        result: getDefaultResult(['Content blocked by safety filters'])
      };
    }

    const result = parseVerificationResponse(rawContent);

    return {
      success: true,
      result: result,
      rawResponse: rawContent
    };

  } catch (error) {
    console.error('[Receipt Verification] Error:', error);
    return {
      success: false,
      error: error.message,
      result: getDefaultResult([error.message])
    };
  }
}

/**
 * Get status display configuration
 */
export function getVerificationStatusDisplay(status) {
  switch (status) {
    case 'APPROVED':
      return {
        color: '#059669',
        bgColor: '#d1fae5',
        label: 'Approved',
        icon: 'shield-check'
      };
    case 'FAILED':
      return {
        color: '#dc2626',
        bgColor: '#fee2e2',
        label: 'Failed',
        icon: 'shield-x'
      };
    case 'MANUAL_REVIEW':
      return {
        color: '#d97706',
        bgColor: '#fef3c7',
        label: 'Manual Review',
        icon: 'shield-alert'
      };
    default:
      return {
        color: '#6366f1',
        bgColor: '#eef2ff',
        label: 'Pending',
        icon: 'shield'
      };
  }
}

/**
 * Get verification check icon and color
 */
export function getCheckDisplay(value) {
  if (value === true) {
    return { icon: 'check', color: '#059669', label: 'Matched' };
  } else if (value === false) {
    return { icon: 'x', color: '#dc2626', label: 'Mismatch' };
  } else if (value === 'UNVERIFIABLE' || value === 'MISSING') {
    return { icon: 'alert', color: '#d97706', label: value };
  }
  return { icon: 'minus', color: '#94a3b8', label: 'N/A' };
}

export default {
  verifyReceipt,
  getVerificationStatusDisplay,
  getCheckDisplay
};
